import React, { useState, useEffect } from 'react';
import './ScheduleCalendar.css';
import { API_ENDPOINTS } from '../../config/apiRouting';
import { useAuth } from '../../contexts/AuthContext';
import authenticatedFetch from '../../utils/authenticatedFetch';
import { ScheduleTemplateManager } from './ScheduleTemplateManager';

interface ScheduleEntry {
  id: string;
  employeeId: string;
  scheduleDate: string;
  startTime: string;
  endTime: string;
  position: string;
  department: string;
  isConflict: boolean;
  conflictReason?: string;
  status: 'scheduled' | 'confirmed' | 'cancelled' | 'completed';
  employee: {
    id: string;
    firstName: string;
    lastName: string;
    employeeNumber: string;
  };
  store: {
    id: string;
    name: string;
  };
}

interface Employee {
  id: string;
  firstName: string;
  lastName: string;
  employeeNumber: string;
  availability: any;
}

interface WeatherData {
  date: string;
  temperature: number;
  high: number;
  low: number;
  condition: string;
  precipitation: number;
  precipitationAmount: number;
  icon: number;
  wind: number;
}

interface ScheduleCalendarProps {
  storeId: string;
  enterpriseId?: string;
  weekStart?: Date;
}

export const ScheduleCalendar: React.FC<ScheduleCalendarProps> = ({
  storeId,
  enterpriseId,
  weekStart = new Date()
}) => {
  const { getAccessToken } = useAuth();
  const [schedules, setSchedules] = useState<ScheduleEntry[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date>(weekStart);
  const [showAddSchedule, setShowAddSchedule] = useState(false);
  const [showBulkSchedule, setShowBulkSchedule] = useState(false);
  const [showTemplateManager, setShowTemplateManager] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState<ScheduleEntry | null>(null);
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [weatherForecast, setWeatherForecast] = useState<WeatherData[]>([]);
  const [stores, setStores] = useState<any[]>([]);

  const [newSchedule, setNewSchedule] = useState({
    employeeId: '',
    scheduleDate: '',
    startTime: '',
    endTime: '',
    position: '',
    department: '',
    breakMinutes: 0,
    notes: ''
  });

  const [bulkSchedule, setBulkSchedule] = useState({
    employeeIds: [] as string[],
    startDate: '',
    endDate: '',
    startTime: '',
    endTime: '',
    position: '',
    department: '',
    breakMinutes: 0,
    notes: '',
    daysOfWeek: [] as number[],
    skipWeekends: true
  });

  useEffect(() => {
    initializeAuth();
  }, []);

  useEffect(() => {
    if (authToken !== null) { // Allow 'no-auth' token to proceed
      const startOfWeek = getStartOfWeek(selectedDate);
      const endOfWeek = getEndOfWeek(selectedDate);
      fetchSchedules(startOfWeek, endOfWeek);
    }
  }, [selectedDate, storeId, authToken]);

  // Fetch employees only when auth or storeId changes, not on date changes
  useEffect(() => {
    if (authToken !== null) {
      fetchEmployees();
    }
  }, [storeId, authToken]);

  // Fetch stores when component mounts
  useEffect(() => {
    fetchStores();
  }, [enterpriseId]);

  // Fetch weather forecast after stores are loaded
  useEffect(() => {
    if (stores.length > 0) {
      fetchWeatherData();
    }
  }, [storeId, stores]);

  const initializeAuth = async () => {
    try {
      const token = await getAccessToken();
      if (token) {
        setAuthToken(token);
      } else {
        setAuthToken('no-auth');
      }
    } catch (error) {
      console.warn('No authenticated user, proceeding without auth token:', error instanceof Error ? error.message : 'Unknown error');
      setAuthToken('no-auth');
    }
  };

  const getAuthHeaders = () => {
    return {
      'Content-Type': 'application/json',
      ...(authToken && authToken !== 'no-auth' && { 'Authorization': `Bearer ${authToken}` })
    };
  };

  const getStartOfWeek = (date: Date) => {
    const start = new Date(date);
    const day = start.getDay();
    const diff = start.getDate() - day;
    return new Date(start.setDate(diff));
  };

  const getEndOfWeek = (date: Date) => {
    const end = new Date(date);
    const day = end.getDay();
    const diff = end.getDate() - day + 6;
    return new Date(end.setDate(diff));
  };

  const fetchSchedules = async (startDate: Date, endDate: Date) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        storeId: storeId,
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
        limit: '200'
      });

      const response = await authenticatedFetch(`${API_ENDPOINTS.schedules()}?${params}`, {
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          console.warn('Scheduling API requires authentication - using empty schedule data');
          setSchedules([]);
          setError(null);
          return;
        }
        throw new Error(`Failed to fetch schedules: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      setSchedules(data.data || []);
      setError(null);
    } catch (err) {
      console.error('Schedule fetch error:', err instanceof Error ? err.message : 'Unknown error');
      setSchedules([]); // Set empty schedules instead of showing error
      setError(null); // Don't show error for auth issues
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      console.log('Fetching employees for storeId:', storeId);
      
      // Get enterprise ID from the store (assuming we have store context)
      const enterpriseId = '17527771077049luj0w1ek'; // Default enterprise for testing
      
      const response = await authenticatedFetch(`${API_ENDPOINTS.employees(enterpriseId)}&storeId=${storeId}&page=1&limit=50`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Employee API response:', data);
        
        const allEmployees = data.data || data.employees || data || [];
        const activeEmployees = allEmployees.filter((emp: any) => 
          emp.status === 'active' || emp.employmentStatus === 'active' || !emp.status
        );
        
        // Map the API response to our Employee interface
        const mappedEmployees = activeEmployees.map((emp: any) => ({
          id: emp.employeeId || emp.id,
          firstName: emp.firstName,
          lastName: emp.lastName,
          employeeNumber: emp.employeeNumber,
          availability: emp.availability || {}
        }));
        
        setEmployees(mappedEmployees);
        console.log(`Successfully loaded ${mappedEmployees.length} real employees:`, mappedEmployees);
      } else {
        throw new Error(`Employee API returned ${response.status}: ${response.statusText}`);
      }
    } catch (err) {
      console.error('Employee API error:', err instanceof Error ? err.message : 'Unknown error');
      
      // Fallback to mock employees only if API fails
      const mockEmployees = [
        { id: 'emp1', firstName: 'John', lastName: 'Doe', employeeNumber: '001', availability: {} },
        { id: 'emp2', firstName: 'Jane', lastName: 'Smith', employeeNumber: '002', availability: {} },
        { id: 'emp3', firstName: 'Mike', lastName: 'Johnson', employeeNumber: '003', availability: {} },
        { id: 'emp4', firstName: 'Test', lastName: 'Employee', employeeNumber: '004', availability: {} },
        { id: 'emp5', firstName: 'BigDemoTest', lastName: 'Server', employeeNumber: '005', availability: {} }
      ];
      
      setEmployees(mockEmployees);
      console.warn('Using fallback mock employees due to API error');
    }
  };

  const fetchStores = async () => {
    try {
      // Use passed enterpriseId or fallback to default
      const activeEnterpriseId = enterpriseId || '17527771077049luj0w1ek';
      
      const response = await authenticatedFetch(API_ENDPOINTS.stores(), {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        const allStores = data.data || data.stores || [];
        // Filter stores by enterprise if we have an enterpriseId
        const filteredStores = activeEnterpriseId 
          ? allStores.filter((store: any) => store.enterpriseId === activeEnterpriseId)
          : allStores;
        
        setStores(filteredStores);
        console.log('Loaded stores for weather:', filteredStores.length);
      } else {
        console.error('Failed to fetch stores');
        setStores([]);
      }
    } catch (error) {
      console.error('Error fetching stores:', error);
      setStores([]);
    }
  };

  const fetchWeatherData = async () => {
    try {
      console.log('🌤️ Fetching real weather data using Weather Analytics API...');
      
      // First, get a store's location to use for weather
      const defaultStore = stores.find(s => s.storeId === storeId) || stores[0];
      
      if (!defaultStore || !defaultStore.latitude || !defaultStore.longitude) {
        console.warn('No store location available for weather data');
        await fetchWeatherFallback();
        return;
      }

      console.log(`🗝️ Getting AccuWeather location key for ${defaultStore.name} (${defaultStore.latitude}, ${defaultStore.longitude})`);
      
      // Get AccuWeather location key from coordinates using Weather Analytics API
      const geocodeResponse = await authenticatedFetch(API_ENDPOINTS.weatherGeocode(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          address: `${defaultStore.latitude},${defaultStore.longitude}`
        })
      });

      if (!geocodeResponse.ok) {
        throw new Error(`Weather geocoding failed: ${geocodeResponse.status}`);
      }

      const geocodeData = await geocodeResponse.json();
      console.log('🗝️ Location key response:', geocodeData);
      
      if (!geocodeData.success || !geocodeData.locationKey) {
        throw new Error('No location key received from AccuWeather');
      }

      console.log(`✅ Got AccuWeather location key: ${geocodeData.locationKey}`);

      // Now get the weather forecast using the Weather Analytics API
      console.log('🌪️ Fetching 7-day weather forecast from AccuWeather...');
      const weatherResponse = await authenticatedFetch(API_ENDPOINTS.weatherCollect(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          stores: [{
            storeId: defaultStore.storeId,
            name: defaultStore.name,
            latitude: defaultStore.latitude,
            longitude: defaultStore.longitude,
            locationKey: geocodeData.locationKey
          }],
          forecast: 7 // Request 7-day forecast
        })
      });

      if (!weatherResponse.ok) {
        throw new Error(`Weather collection failed: ${weatherResponse.status}`);
      }

      const weatherData = await weatherResponse.json();
      console.log('🌪️ Weather API response:', weatherData);

      if (weatherData.success && weatherData.data && weatherData.data.length > 0) {
        // Use the real weather data from Weather Analytics API
        const forecastData = weatherData.data.map((day: any) => ({
          date: day.date,
          temperature: Math.round((day.high + day.low) / 2),
          high: day.high,
          low: day.low,
          condition: day.condition,
          precipitation: day.precipitation || 0,
          precipitationAmount: day.precipitationAmount || 0,
          icon: day.icon || 1,
          wind: day.wind || 0
        }));
        
        setWeatherForecast(forecastData.slice(0, 7)); // Ensure exactly 7 days
        console.log('✅ Real weather forecast loaded:', forecastData.slice(0, 7));
      } else {
        console.warn('⚠️ No forecast data received from Weather Analytics API, using fallback');
        await fetchWeatherFallback();
      }
    } catch (error) {
      console.error('❌ Error fetching real weather data:', error);
      console.log('🔄 Falling back to mock weather data');
      await fetchWeatherFallback();
    }
  };

  const fetchWeatherFallback = async () => {
    // Fallback with more realistic weather patterns
    const mockWeatherData = [];
    const conditions = [
      { name: 'sunny', temp: 75, precip: 5 },
      { name: 'partly cloudy', temp: 70, precip: 15 },
      { name: 'cloudy', temp: 65, precip: 30 },
      { name: 'light rain', temp: 60, precip: 70 },
      { name: 'rain', temp: 58, precip: 85 },
      { name: 'clear', temp: 72, precip: 0 }
    ];
    
    for (let i = 0; i < 7; i++) {
      const date = new Date();
      date.setDate(date.getDate() + i);
      const baseCondition = conditions[Math.floor(Math.random() * conditions.length)];
      const tempVariation = (Math.random() - 0.5) * 10;
      
      mockWeatherData.push({
        date: date.toISOString().split('T')[0],
        temperature: Math.round(baseCondition.temp + tempVariation),
        high: Math.round(baseCondition.temp + tempVariation + 8),
        low: Math.round(baseCondition.temp + tempVariation - 8),
        condition: baseCondition.name,
        precipitation: baseCondition.precip + Math.round((Math.random() - 0.5) * 20),
        precipitationAmount: baseCondition.precip > 50 ? Math.random() * 0.5 : 0,
        icon: 1,
        wind: Math.round(Math.random() * 15 + 5)
      });
    }
    setWeatherForecast(mockWeatherData);
  };

  const getWeatherIcon = (condition: string) => {
    const conditionLower = condition.toLowerCase();
    if (conditionLower.includes('sunny') || conditionLower.includes('clear')) return '☀️';
    if (conditionLower.includes('partly cloudy') || conditionLower.includes('partly')) return '⛅';
    if (conditionLower.includes('cloudy') || conditionLower.includes('overcast')) return '☁️';
    if (conditionLower.includes('rain') || conditionLower.includes('shower')) return '🌧️';
    if (conditionLower.includes('light rain') || conditionLower.includes('drizzle')) return '🌦️';
    if (conditionLower.includes('snow')) return '❄️';
    if (conditionLower.includes('thunder') || conditionLower.includes('storm')) return '⛈️';
    if (conditionLower.includes('fog') || conditionLower.includes('mist')) return '🌫️';
    if (conditionLower.includes('windy') || conditionLower.includes('wind')) return '💨';
    return '🌤️'; // Default partly cloudy
  };

  const handleAddSchedule = async () => {
    try {
      if (!newSchedule.employeeId || !newSchedule.scheduleDate || 
          !newSchedule.startTime || !newSchedule.endTime) {
        alert('Please fill in all required fields');
        return;
      }

      const response = await authenticatedFetch(`${API_ENDPOINTS.schedules()}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          ...newSchedule,
          storeId: storeId
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create schedule');
      }

      const result = await response.json();
      
      if (result.availabilityCheck?.conflicts?.length > 0) {
        const conflicts = result.availabilityCheck.conflicts
          .map((c: any) => c.reason)
          .join('\n');
        alert(`Schedule created with conflicts:\n${conflicts}`);
      }

      setNewSchedule({
        employeeId: '',
        scheduleDate: '',
        startTime: '',
        endTime: '',
        position: '',
        department: '',
        breakMinutes: 0,
        notes: ''
      });
      setShowAddSchedule(false);
      
      const startOfWeek = getStartOfWeek(selectedDate);
      const endOfWeek = getEndOfWeek(selectedDate);
      fetchSchedules(startOfWeek, endOfWeek);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to create schedule');
    }
  };

  const handleUpdateSchedule = async (scheduleId: string, updates: any) => {
    try {
      const response = await authenticatedFetch(`${API_ENDPOINTS.schedule(scheduleId)}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(updates),
      });

      if (!response.ok) throw new Error('Failed to update schedule');

      const startOfWeek = getStartOfWeek(selectedDate);
      const endOfWeek = getEndOfWeek(selectedDate);
      fetchSchedules(startOfWeek, endOfWeek);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update schedule');
    }
  };

  const handleBulkSchedule = async () => {
    try {
      if (!bulkSchedule.employeeIds.length || !bulkSchedule.startDate || 
          !bulkSchedule.endDate || !bulkSchedule.startTime || !bulkSchedule.endTime) {
        alert('Please fill in all required fields');
        return;
      }

      const schedules = [];
      const startDate = new Date(bulkSchedule.startDate);
      const endDate = new Date(bulkSchedule.endDate);
      
      // Generate schedules for each selected employee and date range
      for (const employeeId of bulkSchedule.employeeIds) {
        const currentDate = new Date(startDate);
        
        while (currentDate <= endDate) {
          const dayOfWeek = currentDate.getDay(); // 0 = Sunday, 6 = Saturday
          
          // Skip if day is not selected or if weekend and skipWeekends is true
          const shouldInclude = bulkSchedule.daysOfWeek.length === 0 || 
                               bulkSchedule.daysOfWeek.includes(dayOfWeek);
          const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
          
          if (shouldInclude && !(bulkSchedule.skipWeekends && isWeekend)) {
            schedules.push({
              employeeId,
              scheduleDate: currentDate.toISOString().split('T')[0],
              startTime: bulkSchedule.startTime,
              endTime: bulkSchedule.endTime,
              position: bulkSchedule.position,
              department: bulkSchedule.department,
              breakMinutes: bulkSchedule.breakMinutes,
              notes: bulkSchedule.notes,
              storeId: storeId
            });
          }
          
          currentDate.setDate(currentDate.getDate() + 1);
        }
      }

      // Create schedules in parallel (but limit concurrency to avoid overwhelming the API)
      const batchSize = 5;
      for (let i = 0; i < schedules.length; i += batchSize) {
        const batch = schedules.slice(i, i + batchSize);
        const promises = batch.map(schedule => 
          fetch(`${API_ENDPOINTS.schedules()}`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(schedule)
          })
        );
        
        await Promise.all(promises);
      }

      alert(`Successfully created ${schedules.length} schedules`);
      
      // Reset form and refresh calendar
      setBulkSchedule({
        employeeIds: [],
        startDate: '',
        endDate: '',
        startTime: '',
        endTime: '',
        position: '',
        department: '',
        breakMinutes: 0,
        notes: '',
        daysOfWeek: [],
        skipWeekends: true
      });
      setShowBulkSchedule(false);
      
      const startOfWeek = getStartOfWeek(selectedDate);
      const endOfWeek = getEndOfWeek(selectedDate);
      fetchSchedules(startOfWeek, endOfWeek);
      
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to create bulk schedules');
    }
  };

  const handleEmployeeSelection = (employeeId: string) => {
    setBulkSchedule(prev => ({
      ...prev,
      employeeIds: prev.employeeIds.includes(employeeId)
        ? prev.employeeIds.filter(id => id !== employeeId)
        : [...prev.employeeIds, employeeId]
    }));
  };

  const handleDayOfWeekSelection = (day: number) => {
    setBulkSchedule(prev => ({
      ...prev,
      daysOfWeek: prev.daysOfWeek.includes(day)
        ? prev.daysOfWeek.filter(d => d !== day)
        : [...prev.daysOfWeek, day]
    }));
  };

  const applyTemplateToWeek = async (template: any) => {
    // Confirm before applying template
    const weekStart = getStartOfWeek(selectedDate);
    const weekEnd = getEndOfWeek(selectedDate);
    const confirmMessage = `Are you sure you want to apply the template "${template.name}" to the week of ${weekStart.toLocaleDateString()} - ${weekEnd.toLocaleDateString()}?\n\nThis will create new schedule entries based on the template's par levels. Existing schedules will not be modified.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setLoading(true);
      
      // Get the current week's date range
      const startOfWeek = getStartOfWeek(selectedDate);
      const scheduleEntries = [];
      
      // Convert time slots to actual times
      const timeSlotMapping: { [key: string]: { start: string; end: string } } = {
        '6:00 AM - 10:00 AM': { start: '06:00', end: '10:00' },
        '10:00 AM - 2:00 PM': { start: '10:00', end: '14:00' },
        '2:00 PM - 6:00 PM': { start: '14:00', end: '18:00' },
        '6:00 PM - 10:00 PM': { start: '18:00', end: '22:00' },
        '10:00 PM - Close': { start: '22:00', end: '23:59' }
      };

      // Process each day of the week
      for (let dayOffset = 0; dayOffset < 7; dayOffset++) {
        const currentDate = new Date(startOfWeek);
        currentDate.setDate(startOfWeek.getDate() + dayOffset);
        const dateStr = currentDate.toISOString().split('T')[0];
        
        // Get par levels for this day
        const dayParLevels = template.parLevels.filter((p: any) => p.dayOfWeek === dayOffset);
        
        // Group by time slot and position
        const timeSlotGroups = dayParLevels.reduce((groups: any, parLevel: any) => {
          const key = `${parLevel.timeSlot}-${parLevel.position}`;
          if (!groups[key]) {
            groups[key] = parLevel;
          }
          return groups;
        }, {});

        // Create schedules for each time slot and position
        for (const [key, parLevel] of Object.entries(timeSlotGroups) as [string, any][]) {
          const timeMapping = timeSlotMapping[parLevel.timeSlot];
          if (!timeMapping) continue;

          // Use AI suggestion if available, otherwise use ideal staff count
          const staffCount = parLevel.aiSuggestion || parLevel.idealStaff;
          
          // Get available employees for this position
          const availableEmployees = employees.filter(emp => 
            emp.availability && emp.availability[parLevel.position]
          );
          
          // If no position-specific employees, use any available employees
          const employeesToSchedule = availableEmployees.length > 0 
            ? availableEmployees.slice(0, staffCount)
            : employees.slice(0, Math.min(staffCount, employees.length));

          // Create schedule entries for each employee
          for (const employee of employeesToSchedule) {
            scheduleEntries.push({
              employeeId: employee.id,
              scheduleDate: dateStr,
              startTime: timeMapping.start,
              endTime: timeMapping.end,
              position: parLevel.position,
              department: parLevel.position === 'Manager' ? 'Management' : 
                         ['Server', 'Cashier', 'Busser'].includes(parLevel.position) ? 'Front of House' : 'Kitchen',
              breakMinutes: parLevel.position === 'Manager' ? 60 : 30,
              notes: `Applied from template: ${template.name}`,
              storeId: storeId
            });
          }
        }
      }

      // Create schedules in batches to avoid overwhelming the API
      let successCount = 0;
      const batchSize = 10;
      
      for (let i = 0; i < scheduleEntries.length; i += batchSize) {
        const batch = scheduleEntries.slice(i, i + batchSize);
        const promises = batch.map(schedule => 
          fetch(`${API_ENDPOINTS.schedules()}`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(schedule)
          }).then(response => {
            if (response.ok) successCount++;
            return response;
          }).catch(error => {
            console.error('Failed to create schedule:', error);
            return null;
          })
        );
        
        await Promise.allSettled(promises);
      }

      if (successCount === scheduleEntries.length) {
        alert(`✅ Template applied successfully! Created ${successCount} schedule entries for the week.`);
      } else {
        alert(`⚠️ Template partially applied. Created ${successCount} out of ${scheduleEntries.length} schedule entries. Some entries may have failed due to conflicts or validation errors.`);
      }
      
      // Refresh the calendar to show new schedules
      const startOfWeekRefresh = getStartOfWeek(selectedDate);
      const endOfWeekRefresh = getEndOfWeek(selectedDate);
      fetchSchedules(startOfWeekRefresh, endOfWeekRefresh);
      
    } catch (error) {
      console.error('Error applying template:', error);
      alert('Failed to apply template. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (time: string) => {
    const date = new Date(`1970-01-01T${time}`);
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const getSchedulesForDate = (date: Date) => {
    const dateStr = date.toISOString().split('T')[0];
    return schedules.filter(schedule => schedule.scheduleDate === dateStr);
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'scheduled': return 'status-scheduled';
      case 'confirmed': return 'status-confirmed';
      case 'cancelled': return 'status-cancelled';
      case 'completed': return 'status-completed';
      default: return '';
    }
  };

  const generateWeekDays = () => {
    const startOfWeek = getStartOfWeek(selectedDate);
    const days = [];
    
    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      days.push(day);
    }
    
    return days;
  };

  const navigateWeek = (direction: 'prev' | 'next') => {
    const newDate = new Date(selectedDate);
    newDate.setDate(selectedDate.getDate() + (direction === 'next' ? 7 : -7));
    setSelectedDate(newDate);
  };

  if (loading) return <div className="loading">{schedules.length === 0 ? 'Loading schedule...' : 'Applying template...'}</div>;
  if (error) return <div className="error">Error: {error}</div>;

  const weekDays = generateWeekDays();
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  return (
    <div className="schedule-calendar">
      
      <div className="calendar-header">
        <div className="week-navigation">
          <button className="btn btn-secondary" onClick={() => navigateWeek('prev')}>
            ← Previous Week
          </button>
          <h3>
            Week of {weekDays[0].toLocaleDateString()} - {weekDays[6].toLocaleDateString()}
          </h3>
          <button className="btn btn-secondary" onClick={() => navigateWeek('next')}>
            Next Week →
          </button>
        </div>
        
        <div className="header-actions">
          <button 
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowAddSchedule(true);
            }}
            style={{
              backgroundColor: '#007bff',
              color: 'white',
              padding: '10px 20px',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: 'bold',
              zIndex: 1000,
              position: 'relative',
              pointerEvents: 'auto'
            }}
          >
            Add Schedule
          </button>
          <button 
            className="btn btn-secondary"
            onClick={() => setShowBulkSchedule(true)}
          >
            Bulk Schedule
          </button>
          <button 
            className="btn btn-info"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowTemplateManager(true);
            }}
            style={{
              backgroundColor: '#17a2b8',
              color: 'white',
              padding: '10px 20px',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: 'bold',
              position: 'relative',
              zIndex: 10
            }}
          >
            Manage Templates
          </button>
        </div>
      </div>

      <div className="calendar-grid">
        <div className="calendar-header-row">
          {weekDays.map((day, dayIndex) => {
            const dayWeather = weatherForecast[dayIndex];
            return (
              <div key={day.toISOString()} className="day-header">
                <div className="day-name">{dayNames[day.getDay()]}</div>
                <div className="day-date">{day.getDate()}</div>
                {dayWeather && (
                  <div className="day-weather">
                    <span className="weather-icon-small">{getWeatherIcon(dayWeather.condition)}</span>
                    <span className="temp-range-small">{Math.round(dayWeather.high)}°/{Math.round(dayWeather.low)}°</span>
                    {dayWeather.precipitation > 30 && (
                      <span className="precip-small">{dayWeather.precipitation}%</span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="calendar-body">
          {weekDays.map((day) => (
            <div key={day.toISOString()} className="day-column">
              {getSchedulesForDate(day).map((schedule) => (
                <div
                  key={schedule.id}
                  className={`schedule-item ${getStatusClass(schedule.status)} ${
                    schedule.isConflict ? 'has-conflict' : ''
                  }`}
                  onClick={() => setSelectedSchedule(schedule)}
                >
                  <div className="schedule-time">
                    {formatTime(schedule.startTime)} - {formatTime(schedule.endTime)}
                  </div>
                  <div className="schedule-employee">
                    {schedule.employee.firstName} {schedule.employee.lastName}
                  </div>
                  <div className="schedule-position">{schedule.position}</div>
                  {schedule.isConflict && (
                    <div className="conflict-indicator">WARNING</div>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {showAddSchedule && (
        <div className="schedule-modal-overlay-unique" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 999999
        }}>
          <div className="schedule-modal-unique" style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '8px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
            zIndex: 9999999,
            position: 'relative',
            border: 'none'
          }}>
            <div className="modal-header">
              <h3>Add New Schedule</h3>
              <button
                className="btn btn-secondary"
                onClick={() => setShowAddSchedule(false)}
              >
                Cancel
              </button>
            </div>
            
            <div className="modal-content">
              <div className="form-grid">
                <div className="form-group">
                  <label>Employee:</label>
                  <select
                    value={newSchedule.employeeId}
                    onChange={(e) => setNewSchedule({...newSchedule, employeeId: e.target.value})}
                    className="form-input"
                    style={{minHeight: '40px', fontSize: '14px'}}
                  >
                    <option value="">Select Employee</option>
                    {employees.length === 0 ? (
                      <option value="" disabled>Loading employees...</option>
                    ) : (
                      employees.map((employee) => (
                        <option key={employee.id} value={employee.id}>
                          {employee.firstName} {employee.lastName} ({employee.employeeNumber})
                        </option>
                      ))
                    )}
                  </select>
                </div>

                <div className="form-group">
                  <label>Date:</label>
                  <input
                    type="date"
                    value={newSchedule.scheduleDate}
                    onChange={(e) => setNewSchedule({...newSchedule, scheduleDate: e.target.value})}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label>Start Time:</label>
                  <input
                    type="time"
                    value={newSchedule.startTime}
                    onChange={(e) => setNewSchedule({...newSchedule, startTime: e.target.value})}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label>End Time:</label>
                  <input
                    type="time"
                    value={newSchedule.endTime}
                    onChange={(e) => setNewSchedule({...newSchedule, endTime: e.target.value})}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label>Position:</label>
                  <input
                    type="text"
                    value={newSchedule.position}
                    onChange={(e) => setNewSchedule({...newSchedule, position: e.target.value})}
                    className="form-input"
                    placeholder="e.g., Server, Cook, Manager"
                  />
                </div>

                <div className="form-group">
                  <label>Department:</label>
                  <input
                    type="text"
                    value={newSchedule.department}
                    onChange={(e) => setNewSchedule({...newSchedule, department: e.target.value})}
                    className="form-input"
                    placeholder="e.g., Front of House, Kitchen"
                  />
                </div>

                <div className="form-group">
                  <label>Break Minutes:</label>
                  <input
                    type="number"
                    value={newSchedule.breakMinutes}
                    onChange={(e) => setNewSchedule({...newSchedule, breakMinutes: parseInt(e.target.value) || 0})}
                    className="form-input"
                    min="0"
                    max="480"
                  />
                </div>

                <div className="form-group full-width">
                  <label>Notes:</label>
                  <textarea
                    value={newSchedule.notes}
                    onChange={(e) => setNewSchedule({...newSchedule, notes: e.target.value})}
                    className="form-input"
                    rows={3}
                    placeholder="Optional notes..."
                  />
                </div>
              </div>

              <div className="modal-actions">
                <button className="btn btn-primary" onClick={handleAddSchedule}>
                  Create Schedule
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedSchedule && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Schedule Details</h3>
              <button
                className="btn btn-secondary"
                onClick={() => setSelectedSchedule(null)}
              >
                Close
              </button>
            </div>
            
            <div className="modal-content">
              <div className="schedule-details">
                <div className="detail-row">
                  <label>Employee:</label>
                  <span>{selectedSchedule.employee.firstName} {selectedSchedule.employee.lastName}</span>
                </div>
                <div className="detail-row">
                  <label>Date:</label>
                  <span>{new Date(selectedSchedule.scheduleDate).toLocaleDateString()}</span>
                </div>
                <div className="detail-row">
                  <label>Time:</label>
                  <span>{formatTime(selectedSchedule.startTime)} - {formatTime(selectedSchedule.endTime)}</span>
                </div>
                <div className="detail-row">
                  <label>Position:</label>
                  <span>{selectedSchedule.position}</span>
                </div>
                <div className="detail-row">
                  <label>Department:</label>
                  <span>{selectedSchedule.department}</span>
                </div>
                <div className="detail-row">
                  <label>Status:</label>
                  <span className={`status-badge ${getStatusClass(selectedSchedule.status)}`}>
                    {selectedSchedule.status}
                  </span>
                </div>
                
                {selectedSchedule.isConflict && (
                  <div className="conflict-details">
                    <label>Conflict:</label>
                    <span className="conflict-reason">{selectedSchedule.conflictReason}</span>
                  </div>
                )}
              </div>

              <div className="modal-actions">
                <select
                  value={selectedSchedule.status}
                  onChange={(e) => handleUpdateSchedule(selectedSchedule.id, { status: e.target.value })}
                  className="status-select"
                >
                  <option value="scheduled">Scheduled</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      )}

      {showBulkSchedule && (
        <div className="modal-overlay">
          <div className="modal bulk-modal">
            <div className="modal-header">
              <h3>Bulk Schedule Creation</h3>
              <button
                className="btn btn-secondary"
                onClick={() => setShowBulkSchedule(false)}
              >
                Cancel
              </button>
            </div>
            
            <div className="modal-content">
              <div className="form-grid">
                <div className="form-group full-width">
                  <label>Select Employees:</label>
                  <div className="employee-selection">
                    {employees.map((employee) => (
                      <label key={employee.id} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={bulkSchedule.employeeIds.includes(employee.id)}
                          onChange={() => handleEmployeeSelection(employee.id)}
                        />
                        {employee.firstName} {employee.lastName} ({employee.employeeNumber})
                      </label>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label>Start Date:</label>
                  <input
                    type="date"
                    value={bulkSchedule.startDate}
                    onChange={(e) => setBulkSchedule({...bulkSchedule, startDate: e.target.value})}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label>End Date:</label>
                  <input
                    type="date"
                    value={bulkSchedule.endDate}
                    onChange={(e) => setBulkSchedule({...bulkSchedule, endDate: e.target.value})}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label>Start Time:</label>
                  <input
                    type="time"
                    value={bulkSchedule.startTime}
                    onChange={(e) => setBulkSchedule({...bulkSchedule, startTime: e.target.value})}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label>End Time:</label>
                  <input
                    type="time"
                    value={bulkSchedule.endTime}
                    onChange={(e) => setBulkSchedule({...bulkSchedule, endTime: e.target.value})}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label>Position:</label>
                  <input
                    type="text"
                    value={bulkSchedule.position}
                    onChange={(e) => setBulkSchedule({...bulkSchedule, position: e.target.value})}
                    className="form-input"
                    placeholder="e.g., Server, Cook, Manager"
                  />
                </div>

                <div className="form-group">
                  <label>Department:</label>
                  <input
                    type="text"
                    value={bulkSchedule.department}
                    onChange={(e) => setBulkSchedule({...bulkSchedule, department: e.target.value})}
                    className="form-input"
                    placeholder="e.g., Front of House, Kitchen"
                  />
                </div>

                <div className="form-group">
                  <label>Break Minutes:</label>
                  <input
                    type="number"
                    value={bulkSchedule.breakMinutes}
                    onChange={(e) => setBulkSchedule({...bulkSchedule, breakMinutes: parseInt(e.target.value) || 0})}
                    className="form-input"
                    min="0"
                    max="480"
                  />
                </div>

                <div className="form-group full-width">
                  <label>Days of Week (leave empty for all days):</label>
                  <div className="days-selection">
                    {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day, index) => (
                      <label key={day} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={bulkSchedule.daysOfWeek.includes(index)}
                          onChange={() => handleDayOfWeekSelection(index)}
                        />
                        {day}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={bulkSchedule.skipWeekends}
                      onChange={(e) => setBulkSchedule({...bulkSchedule, skipWeekends: e.target.checked})}
                    />
                    Skip Weekends
                  </label>
                </div>

                <div className="form-group full-width">
                  <label>Notes:</label>
                  <textarea
                    value={bulkSchedule.notes}
                    onChange={(e) => setBulkSchedule({...bulkSchedule, notes: e.target.value})}
                    className="form-input"
                    rows={3}
                    placeholder="Optional notes..."
                  />
                </div>
              </div>

              <div className="modal-actions">
                <button className="btn btn-primary" onClick={handleBulkSchedule}>
                  Create Bulk Schedules
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showTemplateManager && (() => {
        console.log('Rendering template manager modal, showTemplateManager:', showTemplateManager);
        return (
          <div className="template-modal-overlay-unique" style={{
            position: 'fixed',
            top: '0px',
            left: '0px',
            right: '0px',
            bottom: '0px',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99999999,
            pointerEvents: 'auto'
          }}>
            <div className="template-modal-unique" style={{ 
              maxWidth: '1200px', 
              width: '95%', 
              height: '90vh',
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              overflow: 'hidden',
              position: 'relative',
              zIndex: 999999999,
              boxShadow: '0 10px 50px rgba(0, 0, 0, 0.5)',
              display: 'flex',
              flexDirection: 'column',
              border: '3px solid #17a2b8'
            }}>
              <div className="modal-header" style={{
                padding: '20px',
                borderBottom: '1px solid #dee2e6',
                backgroundColor: '#f8f9fa',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <h3 style={{ margin: 0, color: '#333' }}>Schedule Templates</h3>
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowTemplateManager(false)}
                  style={{
                    padding: '8px 16px',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  Close
                </button>
              </div>
              <div style={{ 
                flex: 1,
                overflow: 'auto',
                backgroundColor: '#ffffff'
              }}>
                <ScheduleTemplateManager 
                  storeId={storeId}
                  enterpriseId={enterpriseId}
                  onTemplateSelect={(template) => {
                    setShowTemplateManager(false);
                    applyTemplateToWeek(template);
                  }}
                />
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};